class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    const filterToggle = this.querySelector("#reveal-filters");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
    if (filterToggle) filterToggle.addEventListener("click", this.toggleFilters.bind(this));

    // Custom sort selector
    let customSortSelectors = document.querySelectorAll(".sort-dropdown__item");
    if (customSortSelectors) {
      customSortSelectors.forEach((option) => option.addEventListener("click", this.customSorting.bind(this)));
    }

    // Initialize radio filters
    this.initRadioFilters();

    // Initialize click outside to close
    this.initClickOutsideClose();
  }

  initRadioFilters() {
    document.querySelectorAll(".facets__radio, .mobile-facets__radio").forEach((radio) => {
      radio.addEventListener("click", (e) => this.handleRadioClick(e));
    });
  }

  initClickOutsideClose() {
    document.addEventListener("click", (e) => {
      // Close desktop filter drawer when clicking outside
      const facetsVertical = document.querySelector(".facets-vertical");
      const revealFilters = document.querySelector("#reveal-filters");
      const facetsWrapper = document.querySelector(".facets-wrapper");

      if (facetsVertical?.classList.contains("filters-open")) {
        // Check if click is outside the filter drawer and the toggle button
        if (!facetsWrapper?.contains(e.target) && !revealFilters?.contains(e.target)) {
          revealFilters?.classList.remove("filters-open");
          facetsVertical?.classList.remove("filters-open");
          document.body.classList.remove("overflow-hidden");
        }
      }

      // Close sort dropdowns when clicking outside
      const sortDropdowns = document.querySelectorAll(".sort-dropdown-desktop, .sort-dropdown-mobile");
      sortDropdowns.forEach((dropdown) => {
        const parentDetails = dropdown.closest("details");
        if (parentDetails?.hasAttribute("open") && !parentDetails.contains(e.target)) {
          parentDetails.removeAttribute("open");
        }
      });
    });
  }

  handleRadioClick(event) {
    const radio = event.target;
    const paramName = radio.dataset.paramName;
    const filterGroup = radio.closest('[data-filter-type="radio"]');

    if (!filterGroup) return;

    const wasChecked = radio.dataset.wasChecked === "true";

    if (wasChecked) {
      radio.checked = false;
      radio.dataset.wasChecked = "false";
    } else {
      filterGroup.querySelectorAll('input[type="radio"]').forEach((r) => {
        r.checked = false;
        r.dataset.wasChecked = "false";
      });

      radio.checked = true;
      radio.dataset.wasChecked = "true";
    }

    this.syncRadioForms(paramName, radio.value, radio.checked);
    this.submitRadioFilter(radio);
  }

  syncRadioForms(paramName, value, isChecked) {
    const allRadios = document.querySelectorAll(`input[data-param-name="${paramName}"][value="${value}"]`);
    allRadios.forEach((r) => {
      r.checked = isChecked;
      r.dataset.wasChecked = isChecked ? "true" : "false";
    });

    if (!isChecked) {
      const baseParam = this.getFilterGroupBase(paramName);
      document.querySelectorAll(`input[data-param-name^="${baseParam}"]`).forEach((r) => {
        r.checked = false;
        r.dataset.wasChecked = "false";
      });
    }
  }

  getFilterGroupBase(paramName) {
    return paramName;
  }

  submitRadioFilter(radio) {
    const form = radio.closest("form");
    if (!form) return;

    const formData = new FormData(form);
    const params = new URLSearchParams();
    const radioParamName = radio.dataset.paramName;
    const processedRadioGroups = new Set();

    for (const [key, value] of formData.entries()) {
      const inputElement = form.querySelector(`input[name="${key}"][value="${value}"]`);
      const isRadioInput =
        inputElement &&
        (inputElement.classList.contains("facets__radio") || inputElement.classList.contains("mobile-facets__radio"));

      if (isRadioInput) {
        const inputParamName = inputElement.dataset.paramName;

        if (!processedRadioGroups.has(inputParamName) && inputElement.checked) {
          params.append(inputParamName, value);
          processedRadioGroups.add(inputParamName);
        }
      } else {
        params.append(key, value);
      }
    }

    const searchParams = params.toString();
    FacetFiltersForm.renderPage(searchParams, { target: radio });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById("ProductCountDesktop");
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document.getElementById("ProductGridContainer").querySelector(".collection").classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.reinitRadioFilters();
        if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.reinitRadioFilters();
    if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger(html.innerHTML);
  }

  static reinitRadioFilters() {
    document.querySelectorAll(".facets__radio, .mobile-facets__radio").forEach((radio) => {
      const newRadio = radio.cloneNode(true);
      radio.parentNode.replaceChild(newRadio, radio);

      newRadio.addEventListener("click", (e) => {
        const facetForm = document.querySelector("facet-filters-form");
        if (facetForm) {
          facetForm.handleRadioClick(e);
        }
      });

      if (newRadio.checked) {
        newRadio.dataset.wasChecked = "true";
      }
    });
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    // Update main ProductCount
    const countElement = parsedHTML.getElementById("ProductCount");
    const container = document.getElementById("ProductCount");
    if (countElement && container) {
      container.innerHTML = countElement.innerHTML;
      container.classList.remove("loading");
    }

    // Update desktop ProductCountDesktop
    const countDesktopElement = parsedHTML.getElementById("ProductCountDesktop");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    if (countDesktopElement && containerDesktop) {
      containerDesktop.innerHTML = countDesktopElement.innerHTML;
      containerDesktop.classList.remove("loading");
    }

    // Update mobile ProductCountMobile (in facets-container)
    const allProductCountMobile = parsedHTML.querySelectorAll(".facets-container #ProductCountDesktop");
    const allProductCountMobileDOM = document.querySelectorAll(".facets-container #ProductCountDesktop");
    allProductCountMobile.forEach((el, index) => {
      if (allProductCountMobileDOM[index]) {
        allProductCountMobileDOM[index].innerHTML = el.innerHTML;
      }
    });

    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.renderFilterHeaders(parsedHTML);
    FacetFiltersForm.renderFooterButtons(parsedHTML);

    if (countsToRender && event && event.target) {
      const closestJSFilter = event.target.closest(".js-filter");
      if (closestJSFilter) {
        const closestJSFilterID = closestJSFilter.id;

        if (closestJSFilterID) {
          FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"));
          FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

          const newFacetDetailsElement = document.getElementById(closestJSFilterID);
          const newElementSelector = newFacetDetailsElement.classList.contains("mobile-facets__details")
            ? `.mobile-facets__close-button`
            : `.facets__summary`;
          const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

          const isTextInput = event.target.getAttribute("type") === "text";

          if (newElementToActivate && !isTextInput) newElementToActivate.focus();
        }
      }
    }
  }

  static renderFilterHeaders(html) {
    // Update desktop filter header (with count)
    const desktopHeader = document.querySelector(".facets-drawer__header");
    const desktopHeaderFromHTML = html.querySelector(".facets-drawer__header");
    if (desktopHeader && desktopHeaderFromHTML) {
      desktopHeader.innerHTML = desktopHeaderFromHTML.innerHTML;
    }

    // Update mobile filter header (with count)
    const mobileHeader = document.querySelector(".mobile-facets__header");
    const mobileHeaderFromHTML = html.querySelector(".mobile-facets__header");
    if (mobileHeader && mobileHeaderFromHTML) {
      mobileHeader.innerHTML = mobileHeaderFromHTML.innerHTML;
    }
  }

  static renderActiveFacets(html) {
    // Update active filters container for desktop
    const activeDesktopContainer = document.querySelector(".active-facets-desktop");
    const activeDesktopFromHTML = html.querySelector(".active-facets-desktop");
    if (activeDesktopContainer && activeDesktopFromHTML) {
      activeDesktopContainer.outerHTML = activeDesktopFromHTML.outerHTML;
    }

    // Update active filters container for mobile
    const activeMobileContainer = document.querySelector(".active-facets-mobile");
    const activeMobileFromHTML = html.querySelector(".active-facets-mobile");
    if (activeMobileContainer && activeMobileFromHTML) {
      activeMobileContainer.outerHTML = activeMobileFromHTML.outerHTML;
    }

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderFooterButtons(html) {
    // Update desktop footer
    const desktopFooter = document.querySelector(".facets-drawer__footer");
    const desktopFooterFromHTML = html.querySelector(".facets-drawer__footer");

    if (desktopFooter && desktopFooterFromHTML) {
      desktopFooter.outerHTML = desktopFooterFromHTML.outerHTML;

      // Re-attach event listener to new Apply button
      const newDesktopFooter = document.querySelector(".facets-drawer__footer");
      const newApplyBtn = newDesktopFooter?.querySelector(".facets-drawer-apply");
      if (newApplyBtn) {
        newApplyBtn.addEventListener("click", function () {
          const revealFilters = document.querySelector("#reveal-filters");
          const facetsVertical = document.querySelector(".facets-vertical");
          if (revealFilters) revealFilters.classList.remove("filters-open");
          if (facetsVertical) facetsVertical.classList.remove("filters-open");
          document.body.classList.remove("overflow-hidden");
        });
      }
    }

    // Update mobile footer
    const mobileFooter = document.querySelector(".mobile-facets__footer");
    const mobileFooterFromHTML = html.querySelector(".mobile-facets__footer");

    if (mobileFooter && mobileFooterFromHTML) {
      mobileFooter.outerHTML = mobileFooterFromHTML.outerHTML;

      // Re-attach event listener to new Apply button
      const newMobileFooter = document.querySelector(".mobile-facets__footer");
      const newApplyBtn = newMobileFooter?.querySelector(".mobile-facets-apply");
      if (newApplyBtn) {
        newApplyBtn.addEventListener("click", function () {
          const mobileDrawer = this.closest(".mobile-facets__wrapper");
          if (mobileDrawer) {
            const summary = mobileDrawer.querySelector("summary");
            if (summary) summary.click();
          }
        });
      }
    }
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".sorting",
      ".sort-dropdown-desktop",
      ".sort-dropdown-mobile",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const element = document.querySelector(selector);
      if (element) {
        element.innerHTML = html.querySelector(selector).innerHTML;
      }

      if (selector == ".sort-dropdown-desktop" || selector == ".sort-dropdown-mobile") {
        let customSortSelectorReloaded = document.querySelector(selector)?.querySelectorAll(".sort-dropdown__item");
        customSortSelectorReloaded?.forEach((option2) =>
          option2.addEventListener("click", function () {
            FacetFiltersForm.customSortingReloaded(option2);
          }),
        );
      }
    });

    // Update desktop filter button count
    const desktopFilterCount = html.querySelector("#reveal-filters .facets__open-count");
    const desktopFilterCountDOM = document.querySelector("#reveal-filters .facets__open-count");
    if (desktopFilterCount && desktopFilterCountDOM) {
      desktopFilterCountDOM.outerHTML = desktopFilterCount.outerHTML;
    }

    // Update mobile filter button count (in mobile-facets__open-label)
    const mobileFilterCount = html.querySelector(".mobile-facets__open-label .facets__open-count");
    const mobileFilterCountDOM = document.querySelector(".mobile-facets__open-label .facets__open-count");
    if (mobileFilterCount && mobileFilterCountDOM) {
      mobileFilterCountDOM.outerHTML = mobileFilterCount.outerHTML;
    }

    const mobileFormElement = document.getElementById("FacetFiltersFormMobile");
    if (mobileFormElement) {
      const menuDrawer = mobileFormElement.closest("menu-drawer");
      if (menuDrawer) {
        menuDrawer.bindEvents();
      }
    }

    // Re-render quick filters section
    const quickFiltersFromHTML = html.querySelector("#quick-filters-section");
    const quickFiltersInDOM = document.querySelector("#quick-filters-section");

    if (quickFiltersFromHTML && quickFiltersInDOM) {
      quickFiltersInDOM.outerHTML = quickFiltersFromHTML.outerHTML;

      // Re-initialize QuickFiltersScroller
      const newQuickFiltersWrapper = document.querySelector(".quick-filters-wrapper");
      if (newQuickFiltersWrapper) {
        new QuickFiltersScroller(newQuickFiltersWrapper);
      }
    }

    // Re-initialize Quick Filters after AJAX update
    if (window.QuickFilterManager) {
      new window.QuickFilterManager();
    }

    if (window.facetAnimations) {
      window.facetAnimations.reinit();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector("show-more-button .label-show-more.hidden"));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) => hiddenItem.classList.replace("hidden", "show-more-item"));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }

    const targetSummary = target.querySelector(".mobile-facets__summary");
    const sourceSummary = source.querySelector(".mobile-facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    if (event.target.classList.contains("facets__radio") || event.target.classList.contains("mobile-facets__radio")) {
      return;
    }

    const sortFilterForms = document.querySelectorAll("facet-filters-form form");

    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(event.target.closest("form"));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm" || form.id === "FacetSortDrawerForm") {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  customSorting(event) {
    let option = event.currentTarget.dataset.value;
    const filterSelect = document.querySelector("#SortBy");

    if (filterSelect.value != option) {
      const formFacets = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
      const form = formFacets.querySelector("form");
      let changeEvent = new Event("input");

      filterSelect.value = option;
      form.dispatchEvent(changeEvent);

      event.currentTarget.closest("details").removeAttribute("open");
    }
  }

  static customSortingReloaded(option) {
    let optionValue = option.dataset.value;
    const filterSelect = document.querySelector("#SortBy");

    if (filterSelect.value != optionValue) {
      const formFacets = option.closest("facet-filters-form") || document.querySelector("facet-filters-form");
      const form = formFacets.querySelector("form");
      let changeEvent = new Event("input");

      filterSelect.value = optionValue;
      form.dispatchEvent(changeEvent);

      option.closest("details").removeAttribute("open");
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }

  toggleFilters() {
    this.querySelector("#reveal-filters").classList.toggle("filters-open");
    this.parentElement.querySelector(".facets-vertical").classList.toggle("filters-open");
    const isOpen = this.parentElement.querySelector(".facets-vertical").classList.contains("filters-open");
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeSlider = this.querySelector(".range-min");
    this.maxRangeSlider = this.querySelector(".range-max");
    this.minNumberInput = this.querySelector(".price-min");
    this.maxNumberInput = this.querySelector(".price-max");
    this.activeBackground = this.querySelector(".range-active-background");

    if (!this.minRangeSlider || !this.maxRangeSlider || !this.minNumberInput || !this.maxNumberInput) {
      console.error("PriceRange: Required elements not found");
      return;
    }

    this.absoluteMax = Number(this.minRangeSlider.getAttribute("max"));
    this.minRangeSlider.max = this.absoluteMax;
    this.maxRangeSlider.max = this.absoluteMax;
    this.submitTimer = null;
    this.init();
  }

  init() {
    this.attachEventListeners();
    this.updateVisualState();
  }

  attachEventListeners() {
    this.minRangeSlider.addEventListener("input", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.onMinSliderInput(Number(e.target.value));
    });

    this.maxRangeSlider.addEventListener("input", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.onMaxSliderInput(Number(e.target.value));
    });

    this.minRangeSlider.addEventListener("change", () => {
      this.triggerFormSubmit();
    });

    this.maxRangeSlider.addEventListener("change", () => {
      this.triggerFormSubmit();
    });

    this.minNumberInput.addEventListener("keydown", (e) => this.validateKeyPress(e));
    this.maxNumberInput.addEventListener("keydown", (e) => this.validateKeyPress(e));

    this.minNumberInput.addEventListener("input", (e) => {
      this.onMinInputChange(e.target.value);
    });

    this.maxNumberInput.addEventListener("input", (e) => {
      this.onMaxInputChange(e.target.value);
    });

    this.minNumberInput.addEventListener("blur", () => {
      this.validateMinInput();
      this.triggerFormSubmit();
    });

    this.maxNumberInput.addEventListener("blur", () => {
      this.validateMaxInput();
      this.triggerFormSubmit();
    });

    this.minNumberInput.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        this.validateMinInput();
        this.triggerFormSubmit();
      }
    });

    this.maxNumberInput.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        this.validateMaxInput();
        this.triggerFormSubmit();
      }
    });
  }

  onMinSliderInput(value) {
    let newMin = value;
    const currentMax = Number(this.maxRangeSlider.value);

    if (newMin > currentMax) {
      newMin = currentMax;
      this.minRangeSlider.value = newMin;
    }

    this.minNumberInput.value = newMin;
    this.updateVisualState();
  }

  onMaxSliderInput(value) {
    let newMax = value;
    const currentMin = Number(this.minRangeSlider.value);

    if (newMax < currentMin) {
      newMax = currentMin;
      this.maxRangeSlider.value = newMax;
    }

    this.maxNumberInput.value = newMax;
    this.updateVisualState();
  }

  onMinInputChange(value) {
    let newMin = this.parseNumber(value);

    if (newMin > this.absoluteMax) {
      newMin = this.absoluteMax;
    }

    this.minRangeSlider.value = newMin;
    this.updateVisualState();
    this.debouncedFormSubmit();
  }

  onMaxInputChange(value) {
    let newMax = this.parseNumber(value);

    if (newMax > this.absoluteMax) {
      newMax = this.absoluteMax;
    }

    this.maxRangeSlider.value = newMax;
    this.updateVisualState();
    this.debouncedFormSubmit();
  }

  validateMinInput() {
    let value = this.parseNumber(this.minNumberInput.value);
    const maxValue = Number(this.maxNumberInput.value);

    if (value < 0) value = 0;
    if (value > maxValue) value = maxValue;
    if (value > this.absoluteMax) value = this.absoluteMax;

    this.minNumberInput.value = value;
    this.minRangeSlider.value = value;
    this.updateVisualState();
  }

  validateMaxInput() {
    let value = this.parseNumber(this.maxNumberInput.value);
    const minValue = Number(this.minNumberInput.value);

    if (value < minValue) value = minValue;
    if (value > this.absoluteMax) value = this.absoluteMax;
    if (value < 0) value = 0;

    this.maxNumberInput.value = value;
    this.maxRangeSlider.value = value;
    this.updateVisualState();
  }

  validateKeyPress(event) {
    if (event.metaKey || event.ctrlKey) return;

    const allowedKeys = [
      "Tab",
      "Backspace",
      "Delete",
      "Enter",
      "Escape",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];

    const allowedChars = /[0-9]/;

    if (!allowedKeys.includes(event.key) && !allowedChars.test(event.key)) {
      event.preventDefault();
    }
  }

  updateVisualState() {
    const minValue = Number(this.minRangeSlider.value);
    const maxValue = Number(this.maxRangeSlider.value);

    const minPercent = (minValue / this.absoluteMax) * 100;
    const maxPercent = (maxValue / this.absoluteMax) * 100;

    if (this.activeBackground) {
      this.activeBackground.style.left = `${minPercent}%`;
      this.activeBackground.style.width = `${maxPercent - minPercent}%`;
    }
  }

  debouncedFormSubmit() {
    clearTimeout(this.submitTimer);
    this.submitTimer = setTimeout(() => {
      this.triggerFormSubmit();
    }, 800);
  }

  triggerFormSubmit() {
    const currentMin = Number(this.minNumberInput.value);
    const currentMax = Number(this.maxNumberInput.value);

    // Add name only if value changed from default
    if (currentMin > 0) {
      this.minNumberInput.setAttribute("name", this.minNumberInput.dataset.paramName);
    } else {
      this.minNumberInput.removeAttribute("name");
    }

    if (currentMax < this.absoluteMax) {
      this.maxNumberInput.setAttribute("name", this.maxNumberInput.dataset.paramName);
    } else {
      this.maxNumberInput.removeAttribute("name");
    }
    const facetForm = this.closest("facet-filters-form");

    if (!facetForm) {
      console.warn("PriceRange: No facet-filters-form parent found");
      return;
    }

    const formElement = facetForm.querySelector("form");

    if (!formElement) {
      console.warn("PriceRange: No form element found");
      return;
    }

    const inputEvent = new Event("input", {
      bubbles: true,
      cancelable: true,
    });

    formElement.dispatchEvent(inputEvent);
  }

  parseNumber(value) {
    const parsed = Number(value);
    return isNaN(parsed) ? 0 : Math.floor(parsed);
  }

  reinit() {
    this.init();
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

class FacetAnimations {
  constructor() {
    this.init();
  }

  init() {
    this.initFooterShadow();
    this.initDropdownAnimations();
  }

  initFooterShadow() {
    const mobileMain = document.querySelector(".mobile-facets__main");
    const mobileFooter = document.querySelector(".mobile-facets__footer");
    const desktopMain = document.querySelector(".facets-drawer__main");
    const desktopFooter = document.querySelector(".facets-drawer__footer");

    setTimeout(() => {
      this.updateFooterShadow(mobileMain, mobileFooter);
      this.updateFooterShadow(desktopMain, desktopFooter);
    }, 100);

    if (mobileMain && mobileFooter) {
      mobileMain.addEventListener("scroll", () => {
        this.updateFooterShadow(mobileMain, mobileFooter);
      });
    }

    if (desktopMain && desktopFooter) {
      desktopMain.addEventListener("scroll", () => {
        this.updateFooterShadow(desktopMain, desktopFooter);
      });
    }

    const allDetails = document.querySelectorAll(".mobile-facets__details, .facets__disclosure-vertical");
    allDetails.forEach((detail) => {
      detail.addEventListener("toggle", () => {
        this.updateFooterShadow(mobileMain, mobileFooter);
        this.updateFooterShadow(desktopMain, desktopFooter);

        setTimeout(() => {
          this.updateFooterShadow(mobileMain, mobileFooter);
          this.updateFooterShadow(desktopMain, desktopFooter);
        }, 350);
      });
    });

    let resizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.updateFooterShadow(mobileMain, mobileFooter);
        this.updateFooterShadow(desktopMain, desktopFooter);
      }, 250);
    });
  }

  updateFooterShadow(mainElement, footerElement) {
    if (!mainElement || !footerElement) return;

    const mainRect = mainElement.getBoundingClientRect();
    const footerRect = footerElement.getBoundingClientRect();

    const details = mainElement.querySelectorAll("details[open]");
    let hasHiddenContent = false;

    details.forEach((detail) => {
      const detailRect = detail.getBoundingClientRect();
      if (detailRect.bottom > footerRect.top) {
        hasHiddenContent = true;
      }
    });

    const scrollHeight = mainElement.scrollHeight;
    const clientHeight = mainElement.clientHeight;
    const scrollTop = mainElement.scrollTop;
    const hasScroll = scrollHeight > clientHeight;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    const isScrolledToBottom = scrollBottom < 1;

    const shouldShowShadow = hasHiddenContent || (hasScroll && !isScrolledToBottom);

    footerElement.classList.toggle("show-shadow", shouldShowShadow);
  }

  initDropdownAnimations() {
    // Dropdown animations handled by CSS
  }

  reinit() {
    this.init();
  }
}

document.addEventListener("DOMContentLoaded", function () {
  window.facetAnimations = new FacetAnimations();
});
